import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./contexts/AuthContext";

// Pages
import Landing from "./pages/Landing";
import Pricing from "./pages/Pricing";
import Services from "./pages/Services";
import Contact from "./pages/Contact";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import Dashboard from "./pages/Dashboard";
import Tickets from "./pages/Tickets";
import TicketDetail from "./pages/TicketDetail";
import Bookings from "./pages/Bookings";
import ServiceHistory from "./pages/ServiceHistory";
import PaymentSuccess from "./pages/PaymentSuccess";
import AuthCallback from "./pages/AuthCallback";
import InfoPage from "./pages/InfoPage";

// Admin Pages
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminTickets from "./pages/admin/AdminTickets";
import AdminTicketDetail from "./pages/admin/AdminTicketDetail";
import AdminBookings from "./pages/admin/AdminBookings";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminInquiries from "./pages/admin/AdminInquiries";
import AdminLayout from "./pages/admin/AdminLayout";

// Components
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import ChatWidget from "./components/ChatWidget";
import ProtectedRoute from "./components/ProtectedRoute";

import "./App.css";

function AdminRoute({ children }) {
  return (
    <ProtectedRoute>
      <AdminLayout>{children}</AdminLayout>
    </ProtectedRoute>
  );
}

function AppRouter() {
  const location = useLocation();

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Dashboard and Admin routes don't show marketing navbar/footer
  const isDashboard = location.pathname.startsWith('/dashboard') || 
                      location.pathname.startsWith('/tickets') ||
                      location.pathname.startsWith('/bookings') ||
                      location.pathname.startsWith('/service-history') ||
                      location.pathname.startsWith('/admin');

  // Auth callback page doesn't show navbar/footer either
  const isAuthCallback = location.pathname === '/auth/callback';

  return (
    <>
      {!isDashboard && !isAuthCallback && <Navbar />}
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Landing />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/services" element={<Services />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        
        {/* Info Pages */}
        <Route path="/about" element={<InfoPage />} />
        <Route path="/careers" element={<InfoPage />} />
        <Route path="/help" element={<InfoPage />} />
        <Route path="/terms" element={<InfoPage />} />
        <Route path="/privacy" element={<InfoPage />} />
        
        {/* Protected User Routes */}
        <Route path="/dashboard" element={
          <ProtectedRoute><Dashboard /></ProtectedRoute>
        } />
        <Route path="/tickets" element={
          <ProtectedRoute><Tickets /></ProtectedRoute>
        } />
        <Route path="/tickets/:ticketId" element={
          <ProtectedRoute><TicketDetail /></ProtectedRoute>
        } />
        <Route path="/bookings" element={
          <ProtectedRoute><Bookings /></ProtectedRoute>
        } />
        <Route path="/service-history" element={
          <ProtectedRoute><ServiceHistory /></ProtectedRoute>
        } />

        {/* Admin Routes */}
        <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
        <Route path="/admin/tickets" element={<AdminRoute><AdminTickets /></AdminRoute>} />
        <Route path="/admin/tickets/:ticketId" element={<AdminRoute><AdminTicketDetail /></AdminRoute>} />
        <Route path="/admin/bookings" element={<AdminRoute><AdminBookings /></AdminRoute>} />
        <Route path="/admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
        <Route path="/admin/inquiries" element={<AdminRoute><AdminInquiries /></AdminRoute>} />
      </Routes>
      {!isDashboard && !isAuthCallback && <Footer />}
      <ChatWidget />
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster 
          position="top-right" 
          toastOptions={{
            style: {
              background: '#0D1224',
              border: '1px solid rgba(99, 130, 255, 0.12)',
              color: '#F1F5F9'
            }
          }}
        />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
